import { Injectable } from '@angular/core';
import {
  AttendeeKeys,
  AttendeeType,
  Conference,
  ConferenceKeys,
  ConferenceRegistrationStepName,
  Excursion,
  GuestKeys,
  InviteeStatus,
  RegistrantKeys,
  RegistrantModelKeys,
  WizardStepState,
} from 'ag-common-lib/public-api';
import {
  ExcursionInfoTableData,
  ExcursionInfoTableDataKeys,
} from 'ag-common-svc/lib/components/excursions-info-table/excursions-info-table.models';
import { convertSelectedExcursionsToList, getExcursionsMap } from 'ag-common-svc/lib/utils/excursions-data.utils';
import { ConferenceService } from 'ag-common-svc/public-api';
import { BehaviorSubject, combineLatest, map, Observable, shareReplay } from 'rxjs';
import { AttendeesListService } from 'src/app/ag-crm/registrations/attendees-list.service';
import { differenceInYears, isValid, toDate } from 'date-fns';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { ChairmanExtensionConfigurationKeys } from 'ag-common-lib/lib/models/registration/conference-chairman-extension';
import { RegistrantChairmanExtensionKeys } from 'ag-common-lib/lib/models/registration/registrant-chairman-extension';

@Injectable()
export class ConferenceExcursionStatisticService {
  selectedExcursionsDataSource$: Observable<ExcursionInfoTableData[]>;

  private _excursions$ = new BehaviorSubject<Excursion[]>([]);

  private _notSelectedExcursionsDataSource$ = new BehaviorSubject<ExcursionInfoTableData[]>([]);
  notSelectedExcursionsDataSource$ = this._notSelectedExcursionsDataSource$.asObservable();

  constructor(
    private conferenceService: ConferenceService,
    private attendeesListService: AttendeesListService,
  ) {
    this.selectedExcursionsDataSource$ = combineLatest({
      excursions: this._excursions$,
      attendees: this.attendeesListService.attendees$.pipe(
        map(attendees =>
          attendees?.filter(
            attendee =>
              attendee?.[AttendeeKeys.inviteeStatus] !== InviteeStatus.declined &&
              attendee?.[AttendeeKeys.inviteeOutcomeStatus] !== InviteeStatus.cancelled,
          ),
        ),
        shareReplay(1),
      ),
    }).pipe(
      map(({ excursions, attendees }) => {
        const attendeeExcursionsList: ExcursionInfoTableData[] = [];
        const { excursionsMap } = getExcursionsMap(excursions);
        this.createNotSelectedExcursionData(attendees);

        attendees.forEach(attendee => {
          const isInvitee = attendee[AttendeeKeys.attendeeType] === AttendeeType.Invitee;
          const attendeeSelectedExcursions = isInvitee
            ? [
                attendee?.registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.selectedExcursions],
                attendee?.registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.chairmanExtension]?.[
                  RegistrantChairmanExtensionKeys.selectedExcursions
                ],
              ]
            : [
                attendee?.guest?.[GuestKeys.selectedExcursions],
                attendee?.guest?.[GuestKeys.chairmanExtSelectedExcursions],
              ];
          const attendeeData = {
            [ExcursionInfoTableDataKeys.attendeeId]: attendee?.[BaseModelKeys.dbId],
            [ExcursionInfoTableDataKeys.fullName]: [attendee[AttendeeKeys.firstName], attendee[AttendeeKeys.lastName]]
              .filter(Boolean)
              .join(' '),
            [ExcursionInfoTableDataKeys.inviteeEmail]: attendee?.[AttendeeKeys.inviteeEmail],
            [ExcursionInfoTableDataKeys.phoneNumber]: attendee?.[AttendeeKeys.phoneNumber],
            [ExcursionInfoTableDataKeys.attendeeType]: attendee[AttendeeKeys.attendeeType],
            [ExcursionInfoTableDataKeys.attendeeDob]: attendee[AttendeeKeys.dob],
          };

          attendeeSelectedExcursions.filter(Boolean).forEach(selectedExcursions => {
            attendeeExcursionsList.push(
              ...convertSelectedExcursionsToList(excursionsMap, selectedExcursions, attendeeData),
            );
          });
        });

        if (!attendeeExcursionsList?.length) {
          return null;
        }

        return attendeeExcursionsList;
      }),
      shareReplay(1),
    );
  }

  setConference(conference: Conference): void {
    const conferenceExcursions = conference?.[ConferenceKeys.excursions] ?? [];
    const chairmanExtensionExcursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions] ??
      [];
    this._excursions$.next([...conferenceExcursions, ...chairmanExtensionExcursions]);

    const conferenceDbId = conference?.[BaseModelKeys.dbId];
    if (conferenceDbId) {
      this.attendeesListService.setConferenceDbId(conferenceDbId);
    }
  }

  calculateAge = (e): number => {
    if (!e?.attendeeDob) {
      return null;
    }

    const attendeeDob = toDate(e.attendeeDob);
    if (!isValid(attendeeDob)) {
      console.log('Invalid Date');
      return null;
    }
    return differenceInYears(new Date(), attendeeDob);
  };

  createNotSelectedExcursionData(attendees: Attendee[]) {
    let notSelectedData = [];
    attendees.forEach(attendee => {
      const isInvitee = attendee[AttendeeKeys.attendeeType] === AttendeeType.Invitee;
      const attendeeHasMainSelectedExcursions = isInvitee
        ? !!attendee?.registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.selectedExcursions]
        : !!attendee?.guest?.[GuestKeys.selectedExcursions];
      const attendeeHasChairmanSelectedExcursions = isInvitee
        ? !!attendee?.registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.chairmanExtension]?.[
            RegistrantChairmanExtensionKeys.selectedExcursions
          ]
        : !!attendee?.guest?.[GuestKeys.chairmanExtSelectedExcursions];
      const attendeeHasSelectedExcursions = attendeeHasMainSelectedExcursions || attendeeHasChairmanSelectedExcursions;

      if (attendeeHasSelectedExcursions) {
        return;
      }
      const wizardState = attendee?.registrant?.[RegistrantModelKeys.wizardState];
      const excursionState = isInvitee
        ? wizardState?.[ConferenceRegistrationStepName.registrantExcursionYourSelectionStep]
        : wizardState?.[ConferenceRegistrationStepName.registrantExcursionsStep];
      const attendeeData = {
        [ExcursionInfoTableDataKeys.attendeeId]: attendee?.[BaseModelKeys.dbId],
        [ExcursionInfoTableDataKeys.fullName]: [attendee[AttendeeKeys.firstName], attendee[AttendeeKeys.lastName]]
          .filter(Boolean)
          .join(' '),
        [ExcursionInfoTableDataKeys.inviteeEmail]: attendee?.[AttendeeKeys.inviteeEmail],
        [ExcursionInfoTableDataKeys.phoneNumber]: attendee?.[AttendeeKeys.phoneNumber],
        [ExcursionInfoTableDataKeys.attendeeType]: attendee[AttendeeKeys.attendeeType],
        [ExcursionInfoTableDataKeys.attendeeDob]: attendee[AttendeeKeys.dob],
        [ExcursionInfoTableDataKeys.excursionState]: excursionState ?? WizardStepState.initial, // TODO check
      };
      notSelectedData.push(attendeeData);
    });
    this._notSelectedExcursionsDataSource$.next(notSelectedData);
  }
}
